import { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { jsPDF } from 'jspdf';
import { FileText, Download, CheckCircle, AlertCircle, Clock, Eye, ArrowLeft, ArrowRight, UserCheck, Shield, DollarSign, FileSignature } from 'lucide-react';

type AgreementType = 'referral' | 'sdp';
type AgreementStatus = 'draft' | 'sent' | 'signed' | 'active' | 'expired';

interface PartnerInfo {
  fullName: string;
  idNumber: string;
  businessName: string;
  registrationNumber: string;
  physicalAddress: string;
  email: string;
  phone: string;
  signatureDate: string;
}

interface AgreementData {
  type: AgreementType;
  partner: PartnerInfo;
  company: {
    registrationNumber: string;
    address: string;
    signatureDate: string;
  };
  status: AgreementStatus;
}

const emptyData: AgreementData = {
  type: 'referral',
  partner: {
    fullName: '', idNumber: '', businessName: '', registrationNumber: '',
    physicalAddress: '', email: '', phone: '', signatureDate: '',
  },
  company: {
    registrationNumber: '2024/123456/07',
    address: '15 St Andrews Road, Parktown, Johannesburg, 2193',
    signatureDate: '',
  },
  status: 'draft',
};

const COMMISSION_REFERRAL = [
  { tier: 'Goal-to-100', price: 'R197/month', commission: 'R39.40/month', pct: '20%' },
  { tier: 'Goal-to-1K', price: 'R497/month', commission: 'R99.40/month', pct: '20%' },
  { tier: 'Goal-to-10K', price: 'R997/month', commission: 'R199.40/month', pct: '20%' },
];

const COMMISSION_SDP = [
  { tier: 'Goal-to-100', price: 'R197/month', commission: 'R157.60/month', pct: '80%' },
  { tier: 'Goal-to-1K', price: 'R497/month', commission: 'R397.60/month', pct: '80%' },
  { tier: 'Goal-to-10K', price: 'R997/month', commission: 'R797.60/month', pct: '80%' },
];

const STATUS_CONFIG: Record<AgreementStatus, { label: string; icon: typeof Clock; color: string; bg: string }> = {
  draft: { label: 'Draft', icon: Clock, color: 'text-slate-600', bg: 'bg-slate-100' },
  sent: { label: 'Sent', icon: Eye, color: 'text-amber-600', bg: 'bg-amber-50' },
  signed: { label: 'Signed', icon: FileSignature, color: 'text-green-600', bg: 'bg-green-50' },
  active: { label: 'Active', icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50' },
  expired: { label: 'Expired', icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
};

function getAgreementClauses(type: AgreementType) {
  const isReferral = type === 'referral';
  const commissionTable = isReferral ? COMMISSION_REFERRAL : COMMISSION_SDP;
  const commissionPct = isReferral ? '20%' : '80%';
  const minPayout = isReferral ? 'R100' : 'R500';
  const termNotice = isReferral ? '30 days' : '60 days';
  const nonCompeteDuration = isReferral ? '12 months' : '6 months (same customers)';

  return {
    parties: `This Partnership Agreement ("Agreement") is entered into between Chrysalinks Group (Pty) Ltd (Registration Number: ${emptyData.company.registrationNumber}, Address: ${emptyData.company.address}) ("The Company") and the Partner ("The Partner").`,
    term: `- Start Date: [Date]\n- Duration: 12 months, auto-renewing\n- Termination: ${termNotice} written notice`,
    partnerResponsibilities: isReferral
      ? ['Share BizBuilder with their network', 'Use provided referral links and materials', 'Not make false claims or guarantees', 'Comply with brand guidelines', 'Report any customer issues promptly']
      : ['Deliver BizBuilder programs to assigned customers', 'Maintain quality standards', 'Complete required training', 'Report customer progress weekly', 'Participate in partner meetings', 'Comply with all policies'],
    companyResponsibilities: isReferral
      ? ['Provide referral links and marketing materials', 'Track referrals accurately', 'Pay commissions on time', 'Provide partner support', 'Maintain platform quality']
      : ['Provide training and certification', 'Assign customers to Partner', 'Handle billing and payments', 'Provide marketing materials', 'Offer ongoing support', "Protect Partner's customer relationships"],
    commission: {
      pct: commissionPct,
      table: commissionTable,
      frequency: 'Monthly',
      minPayout,
      paymentMethod: 'EFT to registered bank account',
      invoice: 'Provided by Company by 5th of each month',
      paymentDate: 'By 15th of each month',
    },
    termination: isReferral
      ? ['Either party may terminate with 30 days written notice', 'Outstanding commissions paid within 30 days of termination', 'Partner must stop using brand materials immediately', 'Customer relationships transferred to Company']
      : ['Either party may terminate with 60 days written notice', 'Outstanding commissions paid within 30 days of termination', 'Customer relationships transitioned smoothly', `Non-compete: ${nonCompeteDuration}`],
    promotionTiers: isReferral
      ? [
          { referrals: '5', title: 'Referral Partner', commission: '20%' },
          { referrals: '10', title: 'Senior Referral Partner', commission: '25%' },
          { referrals: '25', title: 'Lead Referral Partner', commission: '30%' },
          { referrals: '50+', title: 'Elite Partner', commission: '35%' },
        ]
      : undefined,
    qualityStandards: isReferral ? undefined : [
      { metric: 'Customer satisfaction', target: '>= 90%' },
      { metric: 'Session attendance', target: '>= 85%' },
      { metric: 'Customer progress', target: '>= 70% module completion' },
      { metric: 'Response time', target: '< 24 hours' },
    ],
    training: isReferral ? undefined : [
      { module: 'BizBuilder Foundations', hours: '4', certification: 'Required' },
      { module: '7 Laws Framework', hours: '6', certification: 'Required' },
      { module: 'Sales & Outreach', hours: '4', certification: 'Required' },
      { module: 'Customer Success', hours: '4', certification: 'Required' },
      { module: 'Platform Tools', hours: '2', certification: 'Required' },
    ],
  };
}

const generalTerms = {
  confidentiality: [
    'Partner must keep all business information confidential',
    'No sharing of customer data with third parties',
    'No disclosure of commission rates to customers',
    'Survives termination of agreement',
  ],
  intellectualProperty: [
    'Company retains all brand and IP rights',
    'Partner receives limited license to use marketing materials',
    'No modification of brand materials without approval',
    'License terminates with agreement',
  ],
  limitationOfLiability: [
    'Company not liable for Partner\'s business decisions',
    'Partner not liable for Company\'s platform issues',
    'Maximum liability limited to commission paid in last 3 months',
  ],
  disputeResolution: [
    'Good faith negotiation (14 days)',
    'Mediation (30 days)',
    'Arbitration (if unresolved)',
    'Governed by South African law',
  ],
};

export default function PartnershipAgreement() {
  const [data, setData] = useState<AgreementData>(emptyData);
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const updatePartner = useCallback(<K extends keyof PartnerInfo>(key: K, value: PartnerInfo[K]) => {
    setData(prev => ({ ...prev, partner: { ...prev.partner, [key]: value } }));
    setError(null);
  }, []);

  const updateCompany = useCallback(<K extends keyof AgreementData['company']>(key: K, value: string) => {
    setData(prev => ({ ...prev, company: { ...prev.company, [key]: value } }));
  }, []);

  const clauses = useMemo(() => getAgreementClauses(data.type), [data.type]);

  const requiredFields = useMemo(() => {
    const missing: string[] = [];
    if (!data.partner.fullName) missing.push('Partner full name');
    if (!data.partner.idNumber) missing.push('ID/Passport number');
    if (!data.partner.businessName) missing.push('Business name');
    if (!data.partner.email) missing.push('Email');
    if (!data.partner.phone) missing.push('Phone');
    if (!data.partner.physicalAddress) missing.push('Physical address');
    return missing;
  }, [data]);

  const generatePdf = useCallback(() => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentW = pageW - 2 * margin;
    let y = margin;

    const typeLabel = data.type === 'referral' ? 'REFERRAL PARTNER AGREEMENT' : 'SERVICE DELIVERY PARTNER AGREEMENT';
    const commissionPct = data.type === 'referral' ? '20%' : '80%';

    // Header
    doc.setFillColor(107, 94, 43);
    doc.rect(0, 0, pageW, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(typeLabel, margin, 13);
    doc.setFontSize(8);
    doc.text('Chrysalinks Group (Pty) Ltd', pageW - margin, 13, { align: 'right' });

    y = 28;
    doc.setTextColor(51, 65, 85);

    const section = (title: string) => {
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin, y);
      y += 6;
    };

    const body = (text: string) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(text, contentW);
      for (const line of lines) {
        if (y > 275) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 4.5;
      }
      y += 2;
    };

    const list = (items: string[]) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      for (const item of items) {
        if (y > 275) {
          doc.addPage();
          y = margin;
        }
        const lines = doc.splitTextToSize(`• ${item}`, contentW);
        for (const line of lines) {
          doc.text(line, margin, y);
          y += 4.5;
        }
      }
      y += 2;
    };

    section('1. PARTIES');
    body(clauses.parties);

    section('2. TERM');
    body(clauses.term);

    section(`3. ${data.type === 'referral' ? 'PARTNER' : 'SERVICE DELIVERY PARTNER'} RESPONSIBILITIES`);
    list(clauses.partnerResponsibilities);

    section('4. COMPANY RESPONSIBILITIES');
    list(clauses.companyResponsibilities);

    section('5. COMMISSION STRUCTURE');
    body(`Commission: ${commissionPct} of customer spending`);
    // Commission table
    if (y > 250) { doc.addPage(); y = margin; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    const colX = [margin, margin + 50, margin + 100, margin + 140];
    doc.text('Tier', colX[0], y);
    doc.text('Customer Price', colX[1], y);
    doc.text('Commission', colX[2], y);
    doc.text('Rate', colX[3], y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setDrawColor(107, 94, 43);
    doc.line(margin, y, margin + contentW, y);
    y += 3;
    for (const row of clauses.commission.table) {
      doc.text(row.tier, colX[0], y);
      doc.text(row.price, colX[1], y);
      doc.text(row.commission, colX[2], y);
      doc.text(row.pct, colX[3], y);
      y += 4.5;
    }
    y += 2;

    section('6. PAYMENT TERMS');
    body(`Frequency: ${clauses.commission.frequency}\nMinimum payout: ${clauses.commission.minPayout}\nPayment method: ${clauses.commission.paymentMethod}\nInvoice: ${clauses.commission.invoice}\nPayment date: ${clauses.commission.paymentDate}`);

    if (clauses.promotionTiers) {
      section('7. PROMOTION TIERS');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const pColX = [margin, margin + 50, margin + 100];
      doc.text('Referrals Accepted', pColX[0], y);
      doc.text('Promotion', pColX[1], y);
      doc.text('Commission', pColX[2], y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      doc.line(margin, y, margin + contentW, y);
      y += 3;
      for (const row of clauses.promotionTiers) {
        doc.text(row.referrals, pColX[0], y);
        doc.text(row.title, pColX[1], y);
        doc.text(row.commission, pColX[2], y);
        y += 4.5;
      }
      y += 2;
    }

    if (clauses.qualityStandards) {
      section('QUALITY STANDARDS');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const qColX = [margin, margin + 80];
      doc.text('Metric', qColX[0], y);
      doc.text('Target', qColX[1], y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      doc.line(margin, y, margin + contentW, y);
      y += 3;
      for (const row of clauses.qualityStandards) {
        doc.text(row.metric, qColX[0], y);
        doc.text(row.target, qColX[1], y);
        y += 4.5;
      }
      y += 2;
    }

    if (clauses.training) {
      section('TRAINING REQUIREMENTS');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const tColX = [margin, margin + 70, margin + 100];
      doc.text('Module', tColX[0], y);
      doc.text('Hours', tColX[1], y);
      doc.text('Certification', tColX[2], y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      doc.line(margin, y, margin + contentW, y);
      y += 3;
      for (const row of clauses.training) {
        doc.text(row.module, tColX[0], y);
        doc.text(row.hours, tColX[1], y);
        doc.text(row.certification, tColX[2], y);
        y += 4.5;
      }
      y += 2;
    }

    section('TERMINATION');
    list(clauses.termination);

    section('CONFIDENTIALITY');
    list(generalTerms.confidentiality);

    section('INTELLECTUAL PROPERTY');
    list(generalTerms.intellectualProperty);

    section('LIMITATION OF LIABILITY');
    list(generalTerms.limitationOfLiability);

    section('DISPUTE RESOLUTION');
    list(generalTerms.disputeResolution);

    section('GOVERNING LAW');
    body('This Agreement shall be governed by and construed in accordance with the laws of South Africa.');

    // Signature section
    if (y > 240) { doc.addPage(); y = margin; }
    y += 6;
    section('SIGNATURES');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('CHRYSALINKS GROUP (PTY) LTD', margin, y); y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Signature: ${data.company.signatureDate ? '_________________' : '_________________'}`, margin, y); y += 6;
    doc.text('Name: _________________', margin, y); y += 6;
    doc.text('Title: _________________', margin, y); y += 6;
    doc.text(`Date: ${data.company.signatureDate || '_________________'}`, margin, y); y += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('PARTNER', margin, y); y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Signature: ${data.partner.signatureDate ? '_________________' : '_________________'}`, margin, y); y += 6;
    doc.text(`Name: ${data.partner.fullName || '_________________'}`, margin, y); y += 6;
    doc.text(`ID/Passport: ${data.partner.idNumber || '_________________'}`, margin, y); y += 6;
    doc.text(`Date: ${data.partner.signatureDate || '_________________'}`, margin, y);

    // Footer
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(`Chrysalinks Group Partnership Agreement  •  Generated ${new Date().toISOString().split('T')[0]}`, margin, 290);
      doc.text(`Page ${i} of ${pages}`, pageW - margin, 290, { align: 'right' });
    }

    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partnership-agreement-${data.type}-${data.partner.fullName.replace(/\s+/g, '-').toLowerCase() || 'draft'}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, clauses]);

  const advanceStatus = useCallback(() => {
    const order: AgreementStatus[] = ['draft', 'sent', 'signed', 'active', 'expired'];
    const idx = order.indexOf(data.status);
    if (idx < order.length - 1) {
      setData(prev => ({ ...prev, status: order[idx + 1] }));
    }
  }, [data.status]);

  const StatusBagde = STATUS_CONFIG[data.status];

  const steps = ['Agreement Type', 'Partner Details', 'Company Details', 'Review & Sign'];

  if (previewMode) {
    const typeLabel = data.type === 'referral' ? 'Referral Partner' : 'Service Delivery Partner';
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-[2rem] border border-slate-200 p-6 lg:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Preview Agreement</h2>
            <button onClick={() => setPreviewMode(false)}
              className="px-4 py-2 bg-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-200 transition-colors">
              Back to Editor
            </button>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 max-h-[70vh] overflow-y-auto">
            <div className="prose prose-sm max-w-none">
              <h3 className="text-base font-black text-slate-800">{typeLabel} Agreement</h3>
              <p className="text-xs text-slate-600">{clauses.parties}</p>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Term</h4>
              <pre className="text-xs text-slate-600 whitespace-pre-wrap">{clauses.term}</pre>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Partner Responsibilities</h4>
              <ul className="text-xs text-slate-600 space-y-1">{clauses.partnerResponsibilities.map((r, i) => <li key={i}>• {r}</li>)}</ul>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Company Responsibilities</h4>
              <ul className="text-xs text-slate-600 space-y-1">{clauses.companyResponsibilities.map((r, i) => <li key={i}>• {r}</li>)}</ul>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Commission: {clauses.commission.pct}</h4>
              <p className="text-xs text-slate-600">{clauses.commission.table.length} tiers • {clauses.commission.frequency} • Min payout {clauses.commission.minPayout}</p>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Payment Terms</h4>
              <p className="text-xs text-slate-600">{clauses.commission.paymentDate} • {clauses.commission.paymentMethod}</p>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Confidentiality</h4>
              <ul className="text-xs text-slate-600 space-y-1">{generalTerms.confidentiality.map((r, i) => <li key={i}>• {r}</li>)}</ul>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Intellectual Property</h4>
              <ul className="text-xs text-slate-600 space-y-1">{generalTerms.intellectualProperty.map((r, i) => <li key={i}>• {r}</li>)}</ul>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Dispute Resolution</h4>
              <ul className="text-xs text-slate-600 space-y-1">{generalTerms.disputeResolution.map((r, i) => <li key={i}>• {r}</li>)}</ul>
              <h4 className="text-xs font-bold text-slate-700 mt-4">Governing Law</h4>
              <p className="text-xs text-slate-600">South Africa</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={generatePdf}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-colors">
              <Download size={16} />
              Download PDF
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-[2rem] border border-slate-200 p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-3 rounded-2xl text-amber-700">
              <FileText size={24} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Partnership Agreement</h2>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                {data.type === 'referral' ? 'Referral Partner (20% Commission)' : 'Service Delivery Partner (80% Commission)'}
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${StatusBagde.bg}`}>
            <StatusBagde.icon size={14} className={StatusBagde.color} />
            <span className={`text-[10px] font-black uppercase tracking-widest ${StatusBagde.color}`}>{StatusBagde.label}</span>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 border-b border-slate-100">
          {steps.map((s, i) => (
            <button key={s} onClick={() => setStep(i)}
              className={`shrink-0 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                step === i ? 'bg-amber-100 text-amber-700' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
              }`}>
              {s}
            </button>
          ))}
        </div>

        {step === 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Select Agreement Type</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button onClick={() => { setData(prev => ({ ...prev, type: 'referral' })); setError(null); }}
                className={`p-6 rounded-2xl border-2 text-left transition-all ${
                  data.type === 'referral' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}>
                <div className="bg-blue-100 w-10 h-10 rounded-xl flex items-center justify-center mb-3">
                  <Shield size={20} className="text-blue-600" />
                </div>
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Type A: Referral Partner</h3>
                <p className="text-2xl font-black text-amber-600 mt-2">20%</p>
                <p className="text-xs text-slate-500">Commission on customer spending</p>
                <ul className="mt-3 space-y-1">
                  <li className="flex items-center gap-2 text-xs text-slate-600"><CheckCircle size={12} className="text-green-500" /> Passive income</li>
                  <li className="flex items-center gap-2 text-xs text-slate-600"><CheckCircle size={12} className="text-green-500" /> Share BizBuilder with your network</li>
                  <li className="flex items-center gap-2 text-xs text-slate-600"><CheckCircle size={12} className="text-green-500" /> Min payout R100</li>
                </ul>
              </button>
              <button onClick={() => { setData(prev => ({ ...prev, type: 'sdp' })); setError(null); }}
                className={`p-6 rounded-2xl border-2 text-left transition-all ${
                  data.type === 'sdp' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}>
                <div className="bg-green-100 w-10 h-10 rounded-xl flex items-center justify-center mb-3">
                  <UserCheck size={20} className="text-green-600" />
                </div>
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Type B: Service Delivery Partner</h3>
                <p className="text-2xl font-black text-amber-600 mt-2">80%</p>
                <p className="text-xs text-slate-500">Commission on customer spending</p>
                <ul className="mt-3 space-y-1">
                  <li className="flex items-center gap-2 text-xs text-slate-600"><CheckCircle size={12} className="text-green-500" /> Active delivery</li>
                  <li className="flex items-center gap-2 text-xs text-slate-600"><CheckCircle size={12} className="text-green-500" /> Deliver BizBuilder programs</li>
                  <li className="flex items-center gap-2 text-xs text-slate-600"><CheckCircle size={12} className="text-green-500" /> Min payout R500</li>
                </ul>
              </button>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-xs font-black text-slate-700 uppercase tracking-tight">Commission Comparison</p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Tier</th>
                      <th className="text-left py-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Customer Price</th>
                      <th className="text-left py-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Referral (20%)</th>
                      <th className="text-left py-2 text-[10px] font-black uppercase tracking-widest text-slate-500">SDP (80%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMMISSION_REFERRAL.map((r, i) => (
                      <tr key={r.tier} className="border-b border-slate-100">
                        <td className="py-2 font-bold text-slate-700">{r.tier}</td>
                        <td className="py-2 text-slate-600">{r.price}</td>
                        <td className="py-2 text-blue-600 font-bold">{r.commission}</td>
                        <td className="py-2 text-green-600 font-bold">{COMMISSION_SDP[i].commission}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Partner Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Partner Full Name *</label>
                <input type="text" value={data.partner.fullName} onChange={e => updatePartner('fullName', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">ID/Passport Number *</label>
                <input type="text" value={data.partner.idNumber} onChange={e => updatePartner('idNumber', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Business Name *</label>
                <input type="text" value={data.partner.businessName} onChange={e => updatePartner('businessName', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Registration Number</label>
                <input type="text" value={data.partner.registrationNumber} onChange={e => updatePartner('registrationNumber', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Email *</label>
                <input type="email" value={data.partner.email} onChange={e => updatePartner('email', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Phone *</label>
                <input type="tel" value={data.partner.phone} onChange={e => updatePartner('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Physical Address *</label>
                <textarea value={data.partner.physicalAddress} onChange={e => updatePartner('physicalAddress', e.target.value)} rows={2}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Company Details</h3>
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Chrysalinks Group (Pty) Ltd</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Registration Number</label>
                  <input type="text" value={data.company.registrationNumber} onChange={e => updateCompany('registrationNumber', e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Company Address</label>
                  <input type="text" value={data.company.address} onChange={e => updateCompany('address', e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h4 className="text-xs font-black text-blue-800 uppercase tracking-tight mb-3">Agreement Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div><span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Type:</span> <span className="text-blue-800">{data.type === 'referral' ? 'Referral Partner' : 'Service Delivery Partner'}</span></div>
                <div><span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Commission:</span> <span className="text-blue-800">{data.type === 'referral' ? '20%' : '80%'}</span></div>
                <div><span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Term:</span> <span className="text-blue-800">12 months, auto-renewing</span></div>
                <div><span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Notice:</span> <span className="text-blue-800">{data.type === 'referral' ? '30 days' : '60 days'}</span></div>
              </div>
            </div>

            <h4 className="text-xs font-black uppercase tracking-widest text-slate-700">Digital Signatures</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Partner Signature Date</label>
                <input type="date" value={data.partner.signatureDate} onChange={e => updatePartner('signatureDate', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Company Signature Date</label>
                <input type="date" value={data.company.signatureDate} onChange={e => updateCompany('signatureDate', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Review & Sign</h3>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-3">Agreement Status</h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {(['draft', 'sent', 'signed', 'active'] as const).map((s, i) => (
                    <div key={s} className="flex items-center gap-1">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        ['draft', 'sent', 'signed', 'active', 'expired'].indexOf(data.status) >= i
                          ? 'bg-amber-600 text-white'
                          : 'bg-slate-200 text-slate-400'
                      }`}>
                        <span className="text-[8px] font-black">{i + 1}</span>
                      </div>
                      {i < 3 && <div className={`w-4 h-0.5 ${['draft', 'sent', 'signed', 'active', 'expired'].indexOf(data.status) > i ? 'bg-amber-600' : 'bg-slate-200'}`} />}
                    </div>
                  ))}
                </div>
                <button onClick={advanceStatus}
                  disabled={data.status === 'active'}
                  className="px-4 py-2 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 disabled:bg-slate-300 transition-colors">
                  {data.status === 'draft' ? 'Send to Partner' : data.status === 'sent' ? 'Mark Signed' : data.status === 'signed' ? 'Activate' : 'Active'}
                </button>
              </div>
            </div>

            {requiredFields.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1">Missing Required Fields</p>
                <div className="flex flex-wrap gap-2">{requiredFields.map(f => (
                  <span key={f} className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-lg">{f}</span>
                ))}</div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setPreviewMode(true)}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-colors">
                <Eye size={16} />
                Preview Agreement
              </button>
              <button onClick={generatePdf} disabled={requiredFields.length > 0}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 disabled:bg-slate-300 transition-colors">
                <Download size={16} />
                Download PDF
              </button>
            </div>
          </motion.div>
        )}

        <div className="flex justify-between mt-6 pt-6 border-t border-slate-100">
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
            className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 disabled:opacity-30 transition-colors">
            <ArrowLeft size={14} />
            Previous
          </button>
          <button onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))} disabled={step === steps.length - 1}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-200 disabled:opacity-30 transition-colors">
            Next
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
